import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Truck,
  ShieldCheck,
  Palette,
  Handshake,
  Quote as QuoteIcon,
  Star,
} from "lucide-react";
import Hero from "../components/Hero";
import { Reveal, MaskedLines, Counter } from "../components/Reveal";
import {
  categories,
  collections,
  projects,
  posts,
  testimonials,
  brands,
  advantages,
} from "../data/site";

const iconMap = {
  Sparkles,
  Building2,
  Truck,
  ShieldCheck,
  Palette,
  Handshake,
};

export default function Home() {
  const featuredCollections = collections.slice(0, 8);
  const featuredProjects = projects.slice(0, 6);
  const latestPosts = posts.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* ————— Manifesto (numbered chapters) ————— */}
      <section className="relative bg-[#FAFAFA] py-24 md:py-40" data-testid="section-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <Reveal>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-6">
                  Manifesto
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1E1E1E] leading-[1.05] tracking-tight">
                  Yüzeyler; <br />
                  <span className="italic">mekânlara verdiğimiz sözdür.</span>
                </h2>
                <p className="mt-8 text-[#666] leading-relaxed text-[15px] max-w-md">
                  SeraPark, seramik ve porselen dünyasının en rafine
                  üreticilerini tek showroom altında buluşturan bir kürasyon
                  atölyesi. 2007'den bu yana, Türkiye'nin dört bir yanındaki
                  villalar, oteller ve ofis kuleleri; bizim seçtiğimiz
                  yüzeylerle nefes alıyor.
                </p>
                <Link
                  to="/kurumsal"
                  data-testid="manifesto-cta"
                  className="btn-ghost mt-10"
                >
                  Hikâyemiz <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-8 space-y-24 md:space-y-32">
              {[
                {
                  n: "01",
                  title: "Mükemmellik",
                  text: "Her koleksiyon, showroom'a girmeden önce üç aşamalı bir kürasyondan geçer. Yüzey teknolojisi, renk uyumu ve mimari uyum — bir yüzeyi seçmemiz için üç şartın da yerini bulması gerekir.",
                  img: "/catalog/vesna-hero.jpg",
                },
                {
                  n: "02",
                  title: "Zamansızlık",
                  text: "Trendleri değil, on yıl sonra bile içinde yaşamaktan mutlu olacağınız yüzeyleri sunuyoruz. İtalyan mermer geleneği, dijital baskı teknolojisiyle buluştuğunda ortaya çıkan sessiz güzelliğe inanıyoruz.",
                  img: "/catalog/calacatta-hero.jpg",
                },
                {
                  n: "03",
                  title: "Kusursuz Ustalık",
                  text: "Ustalık, ayrıntının hikâyesidir. 120×260 cm porselen dilimin ek yerinden, banyo derz genişliğine kadar her karar; showroom'da başlar, mimarınızla devam eder, şantiyede tamamlanır.",
                  img: "/catalog/dolomite-hero.jpg",
                },
              ].map((c, idx) => (
                <Reveal key={c.n} delay={0.1} y={60}>
                  <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-4 flex md:block items-baseline gap-4">
                      <div className="font-serif-display text-[#B8860B] text-6xl md:text-7xl leading-none">
                        {c.n}
                      </div>
                      <h3 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E] md:mt-6 leading-tight">
                        {c.title}
                      </h3>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                      <div className="clip-frame aspect-[16/10] mb-6 group">
                        <img
                          src={c.img}
                          alt={c.title}
                          loading="lazy"
                          className="w-full h-full object-cover img-zoom"
                        />
                      </div>
                      <p className="text-[15px] text-[#555] leading-[1.9] max-w-2xl">
                        {c.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ————— Marquee ————— */}
      <section
        className="py-10 bg-[#1E1E1E] text-white border-y border-white/5"
        data-testid="section-marquee"
      >
        <Marquee gradient={false} speed={40}>
          {[
            "Premium Yüzeyler",
            "Zamansız Tasarım",
            "İtalyan Çizgisi",
            "Showroom Kürasyonu",
            "Türkiye Geneli Sevkiyat",
            "20 Yıl Garanti",
            "Bütünsel Yaklaşım",
          ].flatMap((t, i) => [
            <span key={`t-${i}`} className="marquee-item">
              <span className="font-serif-display text-[38px] md:text-[54px] tracking-tight text-white/90 italic">
                {t}
              </span>
              <span className="text-[#B8860B] text-2xl">✦</span>
            </span>,
          ])}
        </Marquee>
      </section>

      {/* ————— Categories (Bento) ————— */}
      <section className="py-24 md:py-32 bg-white" data-testid="section-categories">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Kategoriler
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1E1E1E] leading-tight max-w-2xl">
                Doğru yüzey, doğru mekân için.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-[#555] max-w-md text-[15px] leading-relaxed">
                Seramikten granite, banyodan dış mekâna — SeraPark'ın 6 ana
                kategorisi, projelerin her ölçeğine tasarımla cevap verir.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
            {categories.map((c, idx) => (
              <Link
                key={c.slug}
                to={`/urunler?kategori=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className={`group relative overflow-hidden bg-[#F5F5F5] card-lift ${
                  idx === 0
                    ? "md:col-span-3 md:row-span-2 aspect-[4/5]"
                    : idx === 1
                    ? "md:col-span-3 aspect-[16/9]"
                    : "md:col-span-2 aspect-[4/5]"
                }`}
              >
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover img-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-white">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8860B] mb-2">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl lg:text-4xl leading-tight">
                    {c.name}
                  </h3>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <p className="text-white/70 text-[13px] max-w-xs leading-relaxed line-clamp-2">
                      {c.description}
                    </p>
                    <ArrowRight
                      size={20}
                      className="shrink-0 transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Latest Collections ————— */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="section-collections">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Yeni Koleksiyonlar
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1E1E1E] leading-tight max-w-3xl">
                2026 Kürasyonu.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/urunler"
                className="btn-ghost"
                data-testid="all-collections-cta"
              >
                Tümünü Gör <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredCollections.map((c, idx) => (
              <Reveal key={c.slug} delay={idx * 0.05}>
                <Link
                  to={`/urunler/${c.slug}`}
                  data-testid={`collection-card-${c.slug}`}
                  className="group block"
                >
                  <div className="clip-frame aspect-[4/5] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={c.hero}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#888]">
                        {c.finish} · {c.color}
                      </div>
                      <h3 className="mt-1 text-lg md:text-xl font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                        {c.name}
                      </h3>
                      <div className="mt-1 text-[12px] text-[#666]">
                        {c.sizes.join(" · ")}
                      </div>
                    </div>
                    <ArrowRight
                      size={16}
                      className="mt-2 text-[#888] group-hover:text-[#B8860B] transition-all group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Why SeraPark ————— */}
      <section className="py-24 md:py-32 bg-white" data-testid="section-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <Reveal>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                  Neden SeraPark
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1E1E1E] leading-tight">
                  Yalnızca ürün değil, <span className="italic">deneyim</span>{" "}
                  kürasyonu.
                </h2>

                <div className="mt-12 grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-4xl md:text-5xl font-light text-[#1E1E1E]">
                      <Counter to={17} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Yıl Deneyim
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl md:text-5xl font-light text-[#1E1E1E]">
                      <Counter to={8000} />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      m² Showroom
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl md:text-5xl font-light text-[#1E1E1E]">
                      <Counter to={520} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Referans Proje
                    </div>
                  </div>
                  <div>
                    <div className="text-4xl md:text-5xl font-light text-[#1E1E1E]">
                      <Counter to={40} suffix="+" />
                    </div>
                    <div className="mt-2 text-[11px] tracking-widest uppercase text-[#888]">
                      Marka
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {advantages.map((a, idx) => {
                const Icon = iconMap[a.icon] || Sparkles;
                return (
                  <Reveal key={a.title} delay={idx * 0.05}>
                    <div className="group h-full p-8 bg-[#FAFAFA] border border-black/5 card-lift hover:bg-white transition-colors">
                      <div className="w-12 h-12 border border-[#B8860B]/30 grid place-items-center text-[#B8860B] group-hover:bg-[#B8860B] group-hover:text-white transition-colors">
                        <Icon size={20} strokeWidth={1.4} />
                      </div>
                      <h3 className="mt-6 text-lg font-medium text-[#1E1E1E]">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-[14px] text-[#666] leading-relaxed">
                        {a.text}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ————— Reference Projects (Masonry) ————— */}
      <section
        className="py-24 md:py-32 bg-[#1E1E1E] text-white"
        data-testid="section-projects"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Referanslar
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight max-w-2xl">
                Mekânları imzalayan projeler.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/projeler"
                className="btn-ghost btn-light-ghost"
                data-testid="all-projects-cta"
              >
                Tüm Projeler <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {featuredProjects.map((p, idx) => (
              <Reveal key={p.slug} delay={idx * 0.05}>
                <div className="break-inside-avoid group relative overflow-hidden">
                  <div
                    className={`clip-frame overflow-hidden ${
                      p.height === "tall"
                        ? "aspect-[3/4]"
                        : p.height === "short"
                        ? "aspect-[4/3]"
                        : "aspect-[1/1]"
                    }`}
                  >
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="pt-5 flex items-start justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#B8860B]">
                        {p.location} · {p.year}
                      </div>
                      <h3 className="mt-1 text-xl font-medium text-white">
                        {p.title}
                      </h3>
                    </div>
                    <div className="flex gap-1.5 flex-wrap justify-end max-w-[45%]">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] tracking-wider uppercase text-white/50 border border-white/15 px-2 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Instagram grid ————— */}
      <section className="py-24 md:py-32 bg-white" data-testid="section-insta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-3">
                Instagram
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E]">
                @seramikpark.com
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {collections.slice(8, 20).map((c) => (
              <a
                key={c.slug}
                href="https://www.instagram.com/seramikpark.com"
                target="_blank"
                rel="noreferrer"
                className="group relative aspect-square overflow-hidden bg-[#F5F5F5]"
              >
                <img
                  src={c.hero}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover img-zoom"
                />
                <div className="absolute inset-0 bg-[#B8860B]/0 group-hover:bg-[#B8860B]/40 transition-colors duration-500" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Testimonials (Glass) ————— */}
      <section
        className="relative py-32 md:py-40 overflow-hidden"
        data-testid="section-testimonials"
      >
        <div className="absolute inset-0">
          <img
            src="/catalog/portoro-hero.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1E1E1E]/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center text-white">
          <Reveal>
            <QuoteIcon size={40} className="mx-auto text-[#B8860B]" strokeWidth={1} />
            <div className="mt-8 grid gap-12">
              {testimonials.map((t, i) => (
                <div key={i} className="glass-dark p-10 md:p-14 border-white/5">
                  <div className="flex justify-center gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} size={14} fill="#B8860B" strokeWidth={0} />
                    ))}
                  </div>
                  <p className="font-serif-display text-2xl md:text-3xl leading-relaxed italic text-white/90">
                    "{t.quote}"
                  </p>
                  <div className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#B8860B]">
                    {t.name} — {t.role}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— Brands ————— */}
      <section className="py-20 bg-white border-y border-black/5" data-testid="section-brands">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-3">
                Markalarımız
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E]">
                Kürasyonumuzun ortakları.
              </h2>
            </div>
            <p className="text-[13px] text-[#666] max-w-md leading-relaxed">
              Ana tedarikçimiz <span className="text-[#1E1E1E] font-medium">Anka Seramik</span> başta olmak üzere,
              sektörün öncü üretici ve tedarikçileriyle birlikte çalışıyoruz.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/8">
            {brands.map((b, i) => (
              <div
                key={b}
                data-testid={`brand-${i}`}
                className={`relative bg-white px-8 py-10 flex items-center justify-center group hover:bg-[#FAFAFA] transition-colors ${
                  i === 0 ? "md:col-span-1" : ""
                }`}
              >
                {i === 0 && (
                  <div className="absolute top-3 left-3 text-[9px] tracking-[0.25em] uppercase text-[#B8860B] border border-[#B8860B]/40 px-2 py-0.5">
                    Ana Tedarikçi
                  </div>
                )}
                <span
                  className={`text-center tracking-[0.22em] transition-colors ${
                    i === 0
                      ? "text-[#1E1E1E] text-[17px] md:text-[19px] font-semibold group-hover:text-[#B8860B]"
                      : "text-[#1E1E1E]/70 text-[14px] md:text-[15px] font-medium group-hover:text-[#B8860B]"
                  }`}
                >
                  {b}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Blog preview ————— */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="section-blog">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                Günlük
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1E1E1E] leading-tight max-w-2xl">
                Yüzeyin sanatı.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/blog" className="btn-ghost" data-testid="all-blog-cta">
                Tüm Yazılar <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {latestPosts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group block"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="clip-frame aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="mt-6 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-[#888]">
                    <span className="text-[#B8860B]">{p.category}</span>
                    <span>·</span>
                    <span>{p.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-2xl md:text-[26px] text-[#1E1E1E] leading-snug group-hover:text-[#B8860B] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#666] leading-relaxed line-clamp-2">
                    {p.excerpt}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— CTA banner ————— */}
      <section className="relative py-24 md:py-32 bg-[#1E1E1E] text-white overflow-hidden" data-testid="section-cta">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/catalog/calacatta-hero.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-6">
              Projeniz için
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
              Bir mekân,
              <br />
              <span className="italic">bir yüzey uzağınızda.</span>
            </h2>
            <div className="mt-12 flex flex-wrap justify-center gap-3">
              <Link to="/teklif-al" className="btn-gold" data-testid="cta-quote">
                Teklif Al <ArrowRight size={14} />
              </Link>
              <Link to="/iletisim" className="btn-ghost btn-light-ghost" data-testid="cta-contact">
                Showroom Ziyareti
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
